/**
 * Kontrola faktur SFD - usluga API (kontrakt: scratchpad/kontrola/KONTRAKT.md v1).
 *
 * Wszystkie odpowiedzi backendu ida przez utils.database.success_response,
 * czyli { success, data, message } - funkcje zwracaja juz odpakowane `data`.
 * Bledy: interceptor z api.js rzuca Error z polami `status`, `errorCode`
 * (np. SFD_BRAK_KONFIGURACJI, SFD_LOGOWANIE, SFD_PORTAL, SYNC_W_TOKU) i `data`.
 *
 * Odswiezanie i test logowania chodza po portalu b2b.sfd.pl (kilkadziesiat
 * stron po 0,3 s) - dlatego apiLong (60 s), a nie zwykle api (30 s).
 */
import api, { apiLong } from './api';

const odpakuj = (response) => response?.data?.data ?? response?.data;

/** Nazwa pliku z naglowka Content-Disposition (filename*=UTF-8''… ma pierwszenstwo). */
export const nazwaZContentDisposition = (naglowek) => {
  if (!naglowek) return null;
  const utf = /filename\*=(?:UTF-8|utf-8)''([^;]+)/.exec(naglowek);
  if (utf) { try { return decodeURIComponent(utf[1].trim()); } catch (e) { /* zly procent - nizej */ } }
  const zwykla = /filename="?([^";]+)"?/.exec(naglowek);
  return zwykla ? zwykla[1].trim() : null;
};

export const kontrolaFakturService = {
  /**
   * GET /kontrola-faktur/podsumowanie?od=YYYY-MM-DD&do=YYYY-MM-DD  (albo ?dni=90)
   * zakres: {od, do} lub liczba dni (od/do maja pierwszenstwo po stronie backendu).
   */
  async podsumowanie(zakres = 90) {
    const params = typeof zakres === 'object' && zakres !== null ? { od: zakres.od, do: zakres.do } : { dni: zakres };
    // Faktury kosztowe UFR: domyslnie wykluczone po stronie serwera; 0 = wlacz
    if (typeof zakres === 'object' && zakres !== null && zakres.wyklucz_ufr !== undefined) params.wyklucz_ufr = zakres.wyklucz_ufr;
    const response = await api.get('/kontrola-faktur/podsumowanie', { params });
    return odpakuj(response);
  },

  /**
   * GET /kontrola-faktur/lista
   * params: { filtr, od, do (albo dni), szukaj, strona, na_strone, sklep_id }
   */
  async lista(params = {}) {
    const response = await api.get('/kontrola-faktur/lista', { params });
    return odpakuj(response);
  },

  /**
   * POST /kontrola-faktur/odswiez  body {tryb: 'biezace'|'pelna_historia', od: 'YYYY-MM-DD'}
   * 'biezace' odpowiada synchronicznie (status 'ok'); 'pelna_historia' zwraca 202
   * {sync_id, status: 'w_toku'} - dalej odpytywac statusOdswiezania(sync_id).
   */
  async odswiez(tryb = 'biezace', od = null) {
    const body = { tryb };
    if (od) body.od = od;
    const response = await apiLong.post('/kontrola-faktur/odswiez', body);
    return odpakuj(response);
  },

  /** GET /kontrola-faktur/odswiez/status/<sync_id> -> {status, stron, pobrano, nowych, adresy_pobrane, adresy_do_pobrania, komunikat} */
  async statusOdswiezania(syncId) {
    const response = await api.get(`/kontrola-faktur/odswiez/status/${syncId}`, { silent: true });
    return odpakuj(response);
  },

  /** GET /kontrola-faktur/ustawienia (admin) */
  async ustawienia() {
    const response = await api.get('/kontrola-faktur/ustawienia');
    return odpakuj(response);
  },

  /** POST /kontrola-faktur/ustawienia  body {wlaczone, email, haslo|null} (admin) */
  async zapiszUstawienia(dane) {
    const response = await api.post('/kontrola-faktur/ustawienia', dane);
    return odpakuj(response);
  },

  // ---------- ANEKS 3: pliki ZIP z portalu + kreator ----------

  /** GET /kontrola-faktur/<id>/dodaj-info -> dane dla kreatora (numer, sklep_id, adres, WZ, plik, w_systemie) */
  async dodajInfo(id) {
    const response = await api.get(`/kontrola-faktur/${id}/dodaj-info`);
    return odpakuj(response);
  },

  /** POST /kontrola-faktur/<id>/plik/pobierz -> {plik: {...}} (synchronicznie, kilka-kilkanascie sekund) */
  async pobierzPlikTeraz(id) {
    const response = await apiLong.post(`/kontrola-faktur/${id}/plik/pobierz`, {});
    return odpakuj(response);
  },

  /**
   * POST /kontrola-faktur/<id>/pdf-z-portalu -> {id, plik} : wydruk PDF faktury z portalu SFD zapisany obok
   * wiersza (plik.pdf = true, podglad przez pobierzPdf). Tylko faktury/korekty SFD; portal trwa - apiLong.
   */
  async pobierzPdfZPortalu(id) {
    const response = await apiLong.post(`/kontrola-faktur/${id}/pdf-z-portalu`, {});
    return odpakuj(response);
  },

  urlCennik: (id) => `/kontrola-faktur/${id}/plik/cennik`,
  urlDokument: (id) => `/kontrola-faktur/${id}/plik/dokument`,
  urlPdf: (id) => `/kontrola-faktur/${id}/plik/pdf`,

  /**
   * GET /kontrola-faktur/<id>/plik/pdf -> Blob (application/pdf): PDF z maila obok wiersza
   * (wydruk paragonu Renomy - ANEKS 6e, albo faktura z kodami EAN - ANEKS 6d).
   */
  async pobierzPdf(id) {
    const response = await api.get(`/kontrola-faktur/${id}/plik/pdf`, { responseType: 'blob' });
    return new Blob([response.data], { type: 'application/pdf' });
  },

  /**
   * GET /kontrola-faktur/<id>/plik/cennik|dokument (XML jako attachment) -> File
   * (text/xml). Nazwa z Content-Disposition, inaczej `<numer|id>_<rodzaj>.xml`.
   */
  async pobierzXml(id, rodzaj, numer = null) {
    if (rodzaj !== 'cennik' && rodzaj !== 'dokument') throw new Error(`Nieznany rodzaj pliku: ${rodzaj}`);
    const response = await api.get(`/kontrola-faktur/${id}/plik/${rodzaj}`, { responseType: 'blob' });
    const nazwa = nazwaZContentDisposition(response.headers?.['content-disposition'])
      || `${String(numer || id).replace(/\//g, '-')}_${rodzaj}.xml`;
    const blob = response.data;
    return new File([blob], nazwa, { type: 'text/xml' });
  },

  /** POST /kontrola-faktur/mail/test (admin) - proba polaczenia ze skrzynka (ANEKS 4) */
  async testujPoczte() {
    const response = await apiLong.post('/kontrola-faktur/mail/test', {});
    return odpakuj(response);
  },

  // ---------- ANEKS 5: wplyw plikow ze skryptu Gmail (klucz jednorazowy) ----------

  /** GET /kontrola-faktur/wplyw/status -> {ma_klucz, od, ostatni, ostatnie:[...]} (bez klucza) */
  async wplywStatus() {
    const response = await api.get('/kontrola-faktur/wplyw/status');
    return odpakuj(response);
  },

  /** POST /kontrola-faktur/wplyw/klucz -> {klucz (jawny, RAZ), od}; poprzedni klucz przestaje dzialac */
  async wplywKluczGeneruj() {
    const response = await api.post('/kontrola-faktur/wplyw/klucz', {});
    return odpakuj(response);
  },

  /** DELETE /kontrola-faktur/wplyw/klucz -> uniewaznia klucz */
  async wplywKluczUniewaznij() {
    const response = await api.delete('/kontrola-faktur/wplyw/klucz');
    return odpakuj(response);
  },

  /** GET /kontrola-faktur/wplyw/skrypt -> tekst skryptu Apps Script z podstawionym URL (tekst albo {skrypt}) */
  async wplywSkrypt() {
    const response = await api.get('/kontrola-faktur/wplyw/skrypt', { responseType: 'text', transformResponse: [(d) => d] });
    const dane = response?.data;
    if (typeof dane !== 'string') return dane?.data?.skrypt ?? dane?.skrypt ?? '';
    // Backend moze oddac czysty tekst albo JSON {success, data: {skrypt}} - rozpoznaj po tresci
    const przyciete = dane.trim();
    if (przyciete.startsWith('{')) {
      try { const j = JSON.parse(przyciete); return j?.data?.skrypt ?? j?.skrypt ?? j?.data ?? ''; } catch (e) { /* to jednak tekst */ }
    }
    return dane;
  },

  // ---------- ANEKS 6: paragony Renomy (S02) - lista PDF od Renomy + XML z maila ----------

  /**
   * GET /kontrola-faktur/paragony/lista (admin)
   * -> {miesiace: [{miesiac:'2026-03', na_liscie, w_systemie, brak, z_xml, wgrano, plik_nazwa}], razem: {na_liscie, w_systemie, brak, z_xml}}
   * Brak wgranej listy = pusta tablica `miesiace`.
   */
  async paragonyLista() {
    const response = await api.get('/kontrola-faktur/paragony/lista');
    return odpakuj(response);
  },

  /**
   * POST /kontrola-faktur/paragony/lista (admin, multipart, pole `plik` = .pdf albo .txt, <= 10 MB)
   * -> {wczytano, nowe, zaktualizowane, miesiace: {'2026-03': {na_liscie, w_systemie, brak, z_xml}}, suma_brutto, ostrzezenia: []}
   * Lista bez ani jednego wiersza PA -> 400 z komunikatem po polsku. pdftotext trwa chwile - apiLong.
   */
  async paragonyListaWgraj(file) {
    const formData = new FormData();
    formData.append('plik', file);
    const response = await apiLong.post('/kontrola-faktur/paragony/lista', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return odpakuj(response);
  },

  // ---------- ANEKS 6b: faktura z XML OSOZ-EDI z maila (gdy portal nie dal ZIP-a) ----------

  /**
   * POST /kontrola-faktur/<id>/dodaj-osoz (admin) body {location_id}
   * -> {faktura_id, numer, pozycji, location_id, z_pdf, nauczono}
   * Wiersz z plik.format 'fa3' (XML FA(3) z KSeF, migracja 053): ta sama trasa - backend parsuje parse_fa3_invoice
   * (GTIN = EAN, bez kroku PDF) i oznacza wiersz KSeF jako 'dodana'.
   * 400 BRAK_POKRYCIA: err.data = {brakujace: [{kod_sfd, nazwa, ilosc}], dopasowane: n, komunikat} - OSOZ ma tylko
   * wewnetrzny kod SFD (bez EAN), wiec pozycji NIE da sie pominac powodem: admin dodaje produkt i ponawia (ANEKS 6c).
   * 409 JUZ_W_SYSTEMIE.
   */
  async dodajOsoz(id, { location_id } = {}) {
    const response = await apiLong.post(`/kontrola-faktur/${id}/dodaj-osoz`, { location_id });
    return odpakuj(response);
  },

  /** POST /kontrola-faktur/ustawienia/test (admin) - proba logowania do portalu */
  async testujLogowanie() {
    const response = await apiLong.post('/kontrola-faktur/ustawienia/test', {});
    return odpakuj(response);
  },
};

export default kontrolaFakturService;
